'use client';
import { useState, useEffect, useCallback } from 'react';

// types
import { IPromo } from '@/lib/contentstack-sdk/types/component';

// utils
import { getPromos } from '@/lib/contentstack-sdk';

export const usePromos = () => {
  const [allPromos, setAllPromos] = useState<IPromo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPromos = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const allPromoData = await getPromos();
        setAllPromos(allPromoData);
      } catch (err) {
        setAllPromos([]);
        setLoading(false);
        setError('Error fetching Promos');
      } finally {
        setLoading(false);
      }
    };

    fetchPromos();
  }, []);

  // Get only defualt promos
  const getDefaultPromos = useCallback(() => {
    const defaultPromos = allPromos.filter((promo) => {
      return promo?.design_studios?.location.length === 0;
    });

    return defaultPromos;
  }, [allPromos]);

  // Returns a list of promos based on studio ID, can include all default promos if needed
  const getPromosByLocation = useCallback(
    (sId: number | undefined, includeDefaultPromos: boolean = false) => {
      if (!sId) return allPromos;

      let filteredPromos = [];

      const locationPromos = allPromos.filter((promo) => {
        return promo?.design_studios?.location.includes(sId.toString());
      });

      const defaultPromos = allPromos.filter((promo) => {
        return promo?.design_studios?.location.length === 0;
      });

      if (includeDefaultPromos) {
        filteredPromos = [...locationPromos, ...defaultPromos];
      } else {
        filteredPromos = locationPromos;
      }

      return filteredPromos;
    },
    [allPromos],
  );

  return { allPromos, loading, error, getPromosByLocation, getDefaultPromos };
};
